import { Handshake } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { useParceirosControllerListar } from '@/api/generated/parceiros/parceiros';
import type { ParceiroDaVitrineResponse } from '@/api/generated/model';
import { useTrackingParceiro } from '@/features/parceiros/hooks/useTrackingParceiro';
import { cn } from '@/shared/lib/cn';

/** Mesmo intervalo da V1 — é o que os parceiros já conhecem como "rodízio". */
const MS_POR_RODADA = 15_000;
const POR_RODADA = 2;

/**
 * Carrossel de parceiros no rodapé da sidebar (F4-4).
 *
 * Era o slot vazio do `Sidebar.tsx`, marcado como "F4-4" num comentário.
 *
 * Duas regras vêm da V1 (`Layout.tsx:66-130`) e ficam:
 *   · **dois por vez, de CATEGORIAS DIFERENTES** — dois laboratórios lado a
 *     lado parecem concorrentes brigando pelo mesmo espaço
 *   · **`aspect-[3/1]`**, que é a proporção que o logo é obrigado a ter no
 *     upload. Aqui os dois números finalmente concordam: na V1 o formulário
 *     pedia 2:1 e este slot renderizava 3:1
 *
 * O que muda: `prefers-reduced-motion` para a rotação. Movimento automático e
 * infinito é exatamente o caso que a mídia query existe para desligar, e a V1
 * a ignorava.
 */
export function ParceirosCarousel() {
  // Só quem está no rodízio. O servidor já corta por `ativo`; este componente
  // é enfeite de sidebar e não deve derrubar o layout se a chamada falhar.
  const { data } = useParceirosControllerListar(undefined, {
    query: { staleTime: 10 * 60 * 1000, retry: false },
  });

  const parceiros = useMemo(() => data?.itens ?? [], [data]);
  const [inicio, setInicio] = useState(0);
  const { refDeImpressao, registrarClique } = useTrackingParceiro();

  const rodizioLigado = useRotacaoPermitida() && parceiros.length > POR_RODADA;

  useEffect(() => {
    if (!rodizioLigado) return;

    const intervalo = setInterval(() => {
      setInicio((atual) => (atual + POR_RODADA) % parceiros.length);
    }, MS_POR_RODADA);

    return () => clearInterval(intervalo);
  }, [rodizioLigado, parceiros.length]);

  const visiveis = useMemo(
    () => escolherDaRodada(parceiros, inicio),
    [parceiros, inicio],
  );

  if (visiveis.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Parceiros
      </p>

      {visiveis.map((parceiro) => (
        <a
          key={parceiro.id}
          ref={refDeImpressao(parceiro.id)}
          href={parceiro.link ?? '#'}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => registrarClique(parceiro.id)}
          title={parceiro.nome}
          className="block rounded-lg overflow-hidden border border-border hover:shadow-md transition-shadow"
        >
          {/* Fundo branco fixo: logo com fundo transparente sumiria no tema escuro. */}
          <div className="relative aspect-[3/1] bg-white flex items-center justify-center">
            {parceiro.logoUrl ? (
              // Sem padding: o quadro já é 3:1, a mesma proporção que o servidor
              // exige do logo, então ele preenche exatamente. Padding tiraria
              // altura e o `object-contain` encolheria a imagem, sobrando branco
              // nas laterais.
              <img
                src={parceiro.logoUrl}
                alt={parceiro.nome}
                className="w-full h-full object-contain"
              />
            ) : (
              <Handshake className="w-5 h-5 text-brand/40" />
            )}
          </div>
        </a>
      ))}

      {parceiros.length > POR_RODADA && (
        <div className="flex justify-center gap-1 flex-wrap" aria-hidden="true">
          {Array.from({ length: Math.ceil(parceiros.length / POR_RODADA) }).map((_, rodada) => (
            <span
              key={rodada}
              className={cn(
                'h-1 w-1 rounded-full transition-colors',
                rodada === Math.floor(inicio / POR_RODADA) ? 'bg-brand' : 'bg-muted-foreground/30',
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Escolhe dois parceiros a partir de `inicio`, preferindo categorias distintas.
 *
 * Se não houver nenhum de outra categoria, devolve os dois mesmo assim — melhor
 * repetir categoria que deixar metade do slot vazio.
 */
function escolherDaRodada(
  parceiros: readonly ParceiroDaVitrineResponse[],
  inicio: number,
): ParceiroDaVitrineResponse[] {
  if (parceiros.length === 0) return [];
  if (parceiros.length === 1) return [parceiros[0]!];

  const primeiro = parceiros[inicio % parceiros.length]!;

  for (let salto = 1; salto < parceiros.length; salto++) {
    const candidato = parceiros[(inicio + salto) % parceiros.length]!;
    if (candidato.categoria !== primeiro.categoria) return [primeiro, candidato];
  }

  return [primeiro, parceiros[(inicio + 1) % parceiros.length]!];
}

/**
 * `false` quando o usuário pediu menos movimento no sistema.
 *
 * Sem isto, a sidebar tem um elemento que se move sozinho a cada 15 s durante o
 * dia inteiro de trabalho — e para quem tem sensibilidade vestibular isso não é
 * detalhe.
 */
function useRotacaoPermitida(): boolean {
  const [permitida, setPermitida] = useState(true);

  useEffect(() => {
    const consulta = window.matchMedia('(prefers-reduced-motion: reduce)');
    const aplicar = () => setPermitida(!consulta.matches);

    aplicar();
    consulta.addEventListener('change', aplicar);
    return () => consulta.removeEventListener('change', aplicar);
  }, []);

  return permitida;
}
